import { useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Share2, Copy, Check, X } from "lucide-react";

/**
 * ShareBar — floating share strip for a property.
 * Prominent WhatsApp CTA (India-first) + native share + copy link.
 */
export default function ShareBar({ property, priceLabel, isRent }) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);

  const url = `${window.location.origin}/property/${property.id}`;
  const headline =
    `${property.bhk} BHK ${property.property_type} ${isRent ? "for rent" : "for sale"} in ${property.locality}, ${property.city}`;
  const price = `${priceLabel}${isRent ? "/month" : ""}`;
  const msg =
    `🏠 ${headline}\n💰 ${price}\n📍 ${property.address || ""}${property.locality ? ", " + property.locality : ""}, ${property.city}\n\n${property.description ? property.description.slice(0, 160) + (property.description.length > 160 ? "…" : "") : ""}\n\nSee photos & contact owner directly 👇\n${url}\n\n(Zero brokerage · via To-Let Rentals)`;

  const openWhatsApp = () => {
    const wa = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(wa, "_blank", "noopener");
  };

  const openStatus = () => {
    // WhatsApp Status share — same URL scheme, but users can pick Status
    openWhatsApp();
    setOpen(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: headline, text: msg, url });
      } catch {}
    } else {
      copy();
    }
    setOpen(false);
  };

  return (
    <div data-testid="share-bar" className="mt-4">
      {/* Primary WhatsApp CTA — impossible to miss */}
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          onClick={openWhatsApp}
          data-testid="share-whatsapp"
          className="flex-1 group inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[#25D366] text-white font-semibold shadow-[0_10px_25px_-8px_rgba(37,211,102,0.55)] hover:bg-[#1EBE5A] active:scale-[0.98] transition-all"
        >
          <MessageCircle size={18} />
          Share on WhatsApp
        </button>
        <button
          onClick={() => setOpen(true)}
          data-testid="share-more"
          className="sm:w-14 h-12 rounded-full border border-[#EFE3D0] hover:border-[#7A1E2A]/40 hover:text-[#7A1E2A] transition-colors inline-flex items-center justify-center gap-2 text-sm text-gray-700"
        >
          <Share2 size={16} />
          <span className="sm:hidden">More share options</span>
        </button>
      </div>

      {/* Sheet with more options */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setOpen(false)}>
          <div
            className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md p-6 space-y-3"
            onClick={(e) => e.stopPropagation()}
            data-testid="share-sheet"
          >
            <div className="flex items-center justify-between mb-1">
              <p className="font-display text-lg text-[#7A1E2A]">Share this listing</p>
              <button onClick={() => setOpen(false)} className="p-1 rounded-full hover:bg-gray-100"><X size={18} /></button>
            </div>
            <p className="text-xs text-gray-500 mb-3">Every share helps the owner reach the right renter faster.</p>

            <button onClick={openWhatsApp} data-testid="sheet-whatsapp" className="w-full flex items-center gap-3 p-3 rounded-xl border border-[#EFE3D0] hover:bg-[#25D366]/8">
              <div className="w-10 h-10 rounded-full bg-[#25D366] text-white flex items-center justify-center"><MessageCircle size={18} /></div>
              <div className="text-left">
                <div className="font-semibold text-[#1B0F14]">WhatsApp</div>
                <div className="text-xs text-gray-500">Chat, groups & status</div>
              </div>
            </button>

            <button onClick={nativeShare} data-testid="sheet-native" className="w-full flex items-center gap-3 p-3 rounded-xl border border-[#EFE3D0] hover:bg-[#FBF6EC]">
              <div className="w-10 h-10 rounded-full bg-[#7A1E2A] text-[#C9A24A] flex items-center justify-center"><Share2 size={18} /></div>
              <div className="text-left">
                <div className="font-semibold text-[#1B0F14]">Share via…</div>
                <div className="text-xs text-gray-500">Instagram, Telegram, Gmail, etc.</div>
              </div>
            </button>

            <button onClick={() => { copy(); setOpen(false); }} data-testid="sheet-copy" className="w-full flex items-center gap-3 p-3 rounded-xl border border-[#EFE3D0] hover:bg-[#FBF6EC]">
              <div className="w-10 h-10 rounded-full bg-[#FBF6EC] text-[#7A1E2A] flex items-center justify-center border border-[#C9A24A]/40">
                {copied ? <Check size={18} /> : <Copy size={18} />}
              </div>
              <div className="text-left">
                <div className="font-semibold text-[#1B0F14]">Copy link</div>
                <div className="text-xs text-gray-500 truncate max-w-[240px]">{url}</div>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
